import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ClipboardCheck, Eye, Lock } from 'lucide-react';
import { COURSE_ASSESSMENT_COOLDOWN_HOURS, COURSE_ASSESSMENT_MAX_FAILS } from '@ready/shared';
import { useApi } from '@/lib/api';
import { formatDateTime } from '@/lib/dates';
import { useMe } from '@/lib/me';
import { NameForm } from '@/components/name-form';
import { Badge, Button, Card, ErrorText } from '@/components/ui';
import { Player } from '@/pages/quiz/take';

/**
 * A course's final assessment, taken inside the course: open once every
 * required lesson is done. `?preview=1` lets the course's authors try it
 * without finishing the course; previews are never recorded.
 */
export function CourseAssessmentPage() {
  const { slug = '' } = useParams();
  const [params] = useSearchParams();
  const preview = params.get('preview') === '1';
  const api = useApi();
  const me = useMe();
  const status = useQuery({
    queryKey: ['courseAssessment', slug],
    queryFn: () => api.courseAssessment(slug),
    enabled: !preview,
    retry: false,
  });
  const course = useQuery({
    queryKey: ['learnerCourse', slug],
    queryFn: () => api.learnerCourse(slug),
    enabled: !preview,
  });
  const start = useMutation({ mutationFn: () => api.startCourseAssessment(slug, preview) });

  if (start.data) return <Player attempt={start.data} />;
  const back = (
    <Link
      to={`/c/${slug}`}
      className="-my-2 mb-2 inline-block py-2 text-sm text-muted-foreground hover:text-foreground"
    >
      ← {status.data?.course.title ?? 'Course'}
    </Link>
  );

  if (preview) {
    return (
      <div>
        {back}
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <Card>
            <p className="flex items-center gap-2 text-sm font-medium text-primary">
              <Eye className="size-4" aria-hidden /> Author preview
            </p>
            <h1 className="mt-1 text-2xl font-semibold">Preview the final assessment</h1>
            <p className="mt-3 text-muted-foreground">
              Take it exactly as learners will. Your answers are scored but never recorded: no
              attempts, reports or certificates.
            </p>
            <Button
              className="mt-6 w-full"
              disabled={start.isPending}
              onClick={() => start.mutate()}
            >
              Start preview
            </Button>
            <ErrorText error={start.error} />
          </Card>
          <AssessmentRules />
        </div>
      </div>
    );
  }

  if (status.isPending || course.isPending)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (status.isError) return <ErrorText error={status.error} />;
  const a = status.data;
  const needsName = Boolean(course.data?.certificate && me.data && !me.data.displayName);

  return (
    <div>
      {back}
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <Card>
          <p className="text-sm text-muted-foreground">Final assessment</p>
          <h1 className="mt-1 text-2xl font-semibold">{a.title}</h1>
          {a.description && <p className="mt-3 text-muted-foreground">{a.description}</p>}
          <ul className="mt-4 flex flex-wrap gap-2">
            <Badge>{a.questionCount} questions</Badge>
            <Badge>Results at the end</Badge>
            <Badge>{a.passPercent}% to pass</Badge>
            {a.attempts > 0 && (
              <Badge>
                {a.attempts} attempt{a.attempts === 1 ? '' : 's'} · best {a.bestPercent}%
              </Badge>
            )}
          </ul>

          {a.passed ? (
            <div className="mt-6 rounded-lg bg-primary/10 p-4 text-sm">
              <p className="flex items-center gap-2 font-medium">
                <ClipboardCheck className="size-4 text-primary" aria-hidden /> You passed with{' '}
                {a.bestPercent}%.
              </p>
              <Link to={`/c/${slug}`} className="mt-2 inline-block text-primary">
                Back to the course
              </Link>
            </div>
          ) : !a.unlocked ? (
            <div className="mt-6 rounded-lg bg-muted p-4 text-sm">
              <p className="flex items-center gap-2 font-medium">
                <Lock className="size-4" aria-hidden /> Finish the course first
              </p>
              <p className="mt-1 text-muted-foreground">
                The assessment opens once you've completed every required lesson ({a.requiredLeft}{' '}
                to go).
              </p>
            </div>
          ) : a.retryAt ? (
            <div className="mt-6 rounded-lg bg-muted p-4 text-sm">
              <p className="font-medium">Take a break and review the lessons</p>
              <p className="mt-1 text-muted-foreground">
                After {COURSE_ASSESSMENT_MAX_FAILS} attempts in a row without passing, the next one
                opens {formatDateTime(a.retryAt)}.
              </p>
            </div>
          ) : needsName && me.data ? (
            <div className="mt-6 rounded-lg bg-muted p-4">
              <p className="mb-3 font-medium">Add your name for your certificate</p>
              <NameForm me={me.data} submitLabel="Save and continue" />
            </div>
          ) : (
            <>
              <Button
                className="mt-6 w-full"
                disabled={start.isPending}
                onClick={() => start.mutate()}
              >
                {a.attempted ? 'Try again' : 'Start the assessment'}
              </Button>
            </>
          )}
          <ErrorText error={start.error} />
        </Card>
        <AssessmentRules />
      </div>
    </div>
  );
}

/** How the final assessment works, beside the intro on wide screens and below it on phones. */
function AssessmentRules() {
  return (
    <Card title="How it works">
      <ul className="space-y-3 text-sm text-muted-foreground">
        <li>It opens once you've completed every required lesson.</li>
        <li>
          Work on your own. Results come at the end: your score and skill breakdown, not the
          answers.
        </li>
        <li>
          After {COURSE_ASSESSMENT_MAX_FAILS} attempts in a row without passing, you can try again{' '}
          {COURSE_ASSESSMENT_COOLDOWN_HOURS} hours later.
        </li>
        <li>Passing completes the course and issues your certificate, if the course has one.</li>
      </ul>
    </Card>
  );
}
